// 成績單整頁（training-transcript-list＝legacy transcriptList.jsp：查詢表單＋四動作＋動態成績表＋匯入 modal）。
// 批次 15 Wave 3（B7-3，Gitea issue #13）。掛在 PageBehavior.complexBody（批次 13 hook 先例，
// complex-form 頁型為產生檔紅線，以整頁置換取代「單筆表單」模板；未登錄頁零影響）。
//
// legacy 流程（行號逐檔親讀 2026-09-24，基準＝ePortfolio_work/.../views/training/transcriptList.jsp；
// 詳批次 15 執行文件 §1.3）：
//   - 查詢表單（:655-746）：*學年度/*醫院/*訓練類別/*臨床訓練計畫／臨床教師部門（radio value=0）／
//     訓練階段部門（radio value=1）/*訓練階段名稱／學員 userCode+userId／訓練期間
//     phaseStartDate+phaseEndDate（預設本月頭尾 epdateutil.currentMonnthFirstday/currentMonnthLastday）
//     ＋reset＋說明 ol li 逐字。按鈕（:697-703）：查詢 btnQuery／匯出畫面資料為Excel btnExport
//     （transcript-ACCESS）／匯入學員成績 btnUpload／計算成績 btnCalculate（transcript-UPDATE）；
//     表下存檔 btnSave（:734）。
//   - doQuery(isCalculate)（:175-268）：queryDeptType==0：clinicalDeptId 空/<1→
//     `臨床教師部門必須選取!`（param.phaseTargetId=''）；==1：phaseTargetId 空/<1→
//     `訓練階段名稱必須選取!`（param.clinicalDeptId=''）→getTranscriptListAjax；
//     無資料→`無資料!`。動態表頭（:277-321）：審核通過 select-all（changeDisble）／訓練期間／
//     員工編號/學號／姓名／臨床指導教師＋每個 transcriptTarget `name(weight%)`（同名同權重合併）
//     ＋試算。列（:399-449）：checkbox changeableCheckBox（changeable==1→未勾+enabled；否則
//     已勾+disabled）＋訓練期間 `{start}~{end}`+div phaseName＋score number input maxlength3＋
//     transcriptTargetId＋finalNum disabled。
//   - 試算（:559-594）：>100→100；`totalScore += score*weight/100`；
//     `finalNum=Math.round(totalScore*100,-1)/100`（Math.round 多參數忽略＝round(x*100)/100）；
//     clearNoNum `Math.round(value*100)/100`。
//   - 計算成績（:369-376）→$.confirm（title 三行逐字，type red，確定）→doQuery(true)。
//   - 存檔（:473-492）→insertTranscriptAndDetail（JSON getTableData()：{phaseInstId, id,
//     changeable(勾選?0:1), transcriptDetailList[{score(空→0), transcriptTargetId}]}）→
//     info＋doQuery(false)。
//   - 匯出（:495-513）：rowData 空→`無資料!`；否則 $.fileDownload exportExcel→雛形
//     list export:true（D-1 2a）＋dataEffect。
//   - 匯入學員成績（:515-518/:748-836 dialogModal modal-lg）：*學年度/*醫院/*訓練類別/
//     *臨床訓練計畫/*訓練階段名稱／訓練期間 start+end（value ${today} readonly）／檔案匯入；
//     btnImport（:358-386）：無檔→`請先選擇檔案`→POST importTranscriptListAjax→成功 doQuery+info。
//     雛形＝成功訊息（批次 14 先例）＋0 data-effect。
//
// 雛形簡化（【雛形值待實機校準】，登記進度檔）：
//   - 級聯下拉→種子平列；日期→文字輸入（批次 11/13 先例）；「本月頭尾」＝固定
//     2026/01/01~2026/01/31（DATE_EPOCH 月）；「今日」＝2026/01/05。
//   - transcriptTargetList/成績列＝種子（getTranscriptListAjax）。
//   - 存檔＝逐列 dataSource.update（真呼叫才 data-effect，C5-j）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Checkbox } from '@/components/base/Checkbox';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  TRAINING_JOB_TYPE_OPTIONS,
  TRAINING_PHASE_TARGET_OPTIONS,
  TRAINING_STUDENT_OPTIONS,
  TRANSCRIPT_TARGETS,
} from '@/mocks/resource-rules';

/** 固定「今日」（fixtures DATE_EPOCH＝2026/01/05；不用 Date.now()）【雛形值待實機校準】。 */
const TODAY = '2026/01/05';

/** clearNoNum（:529-533）：限制小數 2 位（legacy Math.round(value*100)/100）。 */
function clearNoNum(value: string): string {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return '';
  }
  return String(Math.round(num * 100) / 100);
}

/** calculate（:535-580）：>100→100；totalScore += score*weight/100；finalNum=round(x*100)/100。 */
function calculateRow(scores: readonly string[]): string {
  let totalScore = 0;
  scores.forEach((score, index) => {
    const target = TRANSCRIPT_TARGETS[index];
    if (target === undefined) {
      return;
    }
    const num = score === '' ? 0 : Number(score);
    const weight = Number(target.weight);
    totalScore += (num * weight) / 100;
  });
  return String(Math.round(totalScore * 100) / 100);
}

export function TranscriptBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 查詢表單（:655-746）。欄位 name 照 legacy（C5-h 送出自報 complexBodySubmitId＝查詢鈕）。
  // ------------------------------------------------------------------
  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState('org-1');
  const [jobTypeId, setJobTypeId] = useState('3');
  const [planTargetId, setPlanTargetId] = useState('plan-1');
  // queryDeptType 預設空（legacy 初始兩個 radio 都 disabled，planTarget 選後才帶 value=1；
  // 雛形 radio 不鎖但預設不選＝驗證鏉分支不觸發掛載查詢【雛形值待實機校準】）。
  const [queryDeptType, setQueryDeptType] = useState('');
  const [clinicalDeptId, setClinicalDeptId] = useState('');
  const [phaseDepartmentId, setPhaseDepartmentId] = useState('');
  const [phaseTargetId, setPhaseTargetId] = useState('sp-1');
  const [userCode, setUserCode] = useState('');
  const [userId, setUserId] = useState('');
  // 訓練期間預設空（legacy 本月頭尾 epdateutil.currentMonnthFirstday/currentMonnthLastday；
  // 雛形以空值讓初始清單不被日期精確比對清空【雛形值待實機校準】）。
  const [phaseStartDate, setPhaseStartDate] = useState('');
  const [phaseEndDate, setPhaseEndDate] = useState('');

  // ------------------------------------------------------------------
  // 成績表（doQuery→getTranscriptListAjax；種子＋filters）。
  // ------------------------------------------------------------------
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [scoreDrafts, setScoreDrafts] = useState<Record<string, Record<string, string>>>({});
  const [finalNums, setFinalNums] = useState<Record<string, string>>({});
  const [changeables, setChangeables] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const appliedRef = useRef<Record<string, string>>({});

  const load = (filters: Record<string, string>, isCalculate: boolean) => {
    void (async () => {
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters });
        const list = result.rows;
        setRows(list);
        // 初始化分數草稿／審核通過勾選／試算（createTableRow :399-449＋calculate）。
        const drafts: Record<string, Record<string, string>> = {};
        const finals: Record<string, string> = {};
        const checks: Record<string, boolean> = {};
        for (const row of list) {
          const rowKey = String(row.id ?? '');
          const detail = row['transcriptDetailList'];
          const draft: Record<string, string> = {};
          const scores: string[] = [];
          for (const target of TRANSCRIPT_TARGETS) {
            const value =
              Array.isArray(detail)
                ? (detail as Row[])
                    .filter((entry) => String(entry['transcriptTargetId'] ?? '') === target.id)
                    .map((entry) => String(entry['score'] ?? ''))[0] ?? ''
                : '';
            draft[target.id] = value;
            scores.push(value);
          }
          drafts[rowKey] = draft;
          finals[rowKey] = calculateRow(scores);
          // changeable==1→未勾+enabled；否則已勾+disabled（:411-414）。
          checks[rowKey] = String(row['changeable'] ?? '') !== '1';
        }
        setScoreDrafts(drafts);
        setFinalNums(finals);
        setChangeables(checks);
        if (list.length === 0) {
          await dialogs.alert('無資料!'); // :262
        }
        // legacy isCalculate=true 由 server 計算成績（不自動存檔；confirm 文字 :374-377 明說
        // 「算完後的成績需要自己按存檔」）→ 雛形不顯示任何成功訊息（R2-F1 修正）。
        void isCalculate;
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  /** doQuery（:136-205）：queryDeptType 分支只做驗證鏉（:159/:165）；filters 帶各欄原值
   *  （C5-h：帶 name 控制項的值要進 DataSource；legacy 的 param.phaseTargetId=''/
   *  clinicalDeptId='' 空白覆寫不鏡像【雛形值待實機校準】）。 */
  const doQuery = (isCalculate = false) => {
    const filters: Record<string, string> = {
      annualId,
      organizationId,
      jobTypeId,
      planTargetId,
      queryDeptType,
      phaseTargetId,
      clinicalDeptId,
      phaseDepartmentId,
      userCode,
      userId,
      phaseStartDate,
      phaseEndDate,
    };
    if (queryDeptType === '0') {
      if (clinicalDeptId === '' || Number(clinicalDeptId) < 1) {
        void dialogs.alert('臨床教師部門必須選取!'); // :159
        return;
      }
    } else if (queryDeptType === '1') {
      if (phaseTargetId === '' || Number(phaseTargetId) < 1) {
        void dialogs.alert('訓練階段名稱必須選取!'); // :165
        return;
      }
    }
    appliedRef.current = filters;
    load(filters, isCalculate);
  };

  /** 匯出（:291-298/:445-464）：無資料→`無資料!`；否則 list export:true（D-1 2a）。 */
  const doExport = () => {
    void (async () => {
      if (rows.length === 0) {
        await dialogs.alert('無資料!');
        return;
      }
      await dataSource.list(pageId, { page: 1, pageSize: 50, export: true, filters: appliedRef.current });
      // legacy $.fileDownload 成功＝瀏覽器下載（雛形 Blob 管線在 list export 模擬）【雛形值待實機校準】。
    })();
  };

  /** 存檔（:423-443）：逐列 update（changeable＋transcriptDetailList；真呼叫才 data-effect）。 */
  const doSave = () => {
    void (async () => {
      setBusy(true);
      try {
        for (const row of rows) {
          const rowKey = String(row.id ?? '');
          if (rowKey === '') {
            continue;
          }
          const transcriptDetailList = TRANSCRIPT_TARGETS.map((target) => ({
            score: scoreDrafts[rowKey]?.[target.id] === undefined || scoreDrafts[rowKey]?.[target.id] === ''
              ? '0'
              : (scoreDrafts[rowKey]?.[target.id] ?? '0'),
            transcriptTargetId: target.id,
          }));
          await dataSource.update(pageId, rowKey, {
            changeable: changeables[rowKey] === true ? '0' : '1',
            transcriptDetailList,
          });
        }
        await dialogs.info(t('state.saveSuccess'));
        doQuery(false);
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 分數變更（reCalculate/clearNoNum/calculate；>100→100 夾限在輸入值上，:538-540）。 */
  const changeScore = (rawKey: string, targetId: string, raw: string) => {
    let value = clearNoNum(raw);
    if (Number(value) > 100) {
      value = '100';
    }
    setScoreDrafts((current) => {
      const next: Record<string, Record<string, string>> = {
        ...current,
        [rawKey]: { ...(current[rawKey] ?? {}), [targetId]: value },
      };
      // 同步試算（ TRANSCRIPT_TARGETS 順序）。
      const scores = TRANSCRIPT_TARGETS.map((target) => next[rawKey]?.[target.id] ?? '');
      setFinalNums((currentFinals) => ({ ...currentFinals, [rawKey]: calculateRow(scores) }));
      return next;
    });
  };

  // ------------------------------------------------------------------
  // 匯入學員成績 modal（dialogModal :748-836；btnImport :358-386）。
  // ------------------------------------------------------------------
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPhaseTargetId, setImportPhaseTargetId] = useState('');
  const runImport = () => {
    void (async () => {
      if (importFile === null) {
        await dialogs.alert('請先選擇檔案'); // :302
        return;
      }
      // 雛形無真後端（批次 14 academicMOM doImport 先例）：成功訊息＋清檔＋reload＋0 data-effect。
      await dialogs.info('匯入成功（雛形不處理檔案內容）【雛形值待實機校準】');
      setImportFile(null);
      setImportOpen(false);
      doQuery(false);
      void importPhaseTargetId;
    })();
  };

  // 首載一次（legacy ready 後手動查詢；雛形 ready 即查詢＝批次 14 ready 即查詢先例）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 首載一次。
  useEffect(() => {
    doQuery(false);
  }, []);

  const headColumns: readonly TableColumn[] = [
    { key: 'check', header: '審核通過', align: 'center' },
    { key: 'period', header: '訓練期間', align: 'center' },
    { key: 'employee', header: '員工編號/學號', align: 'center' },
    { key: 'student', header: '姓名', align: 'center' },
    { key: 'clinical', header: '臨床指導教師', align: 'center' },
    ...TRANSCRIPT_TARGETS.map(
      (target): TableColumn => ({
        key: `target-${target.id}`,
        header: `${target.name}(${target.weight}%)`,
        align: 'center',
      }),
    ),
    { key: 'final', header: '試算', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="成績單"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <div className="grid gap-4 md:grid-cols-7">
          <div className="md:col-span-4 space-y-3" data-ui-id={`${pageId}-query-form`}>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>學年度：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-annual-id`}
                  name="annualId"
                  value={annualId}
                  onChange={(next) => setAnnualId(next)}
                  options={selectOptionsOverride(pageId, 'annualId') ?? COURSE_CLAZZ_ANNUAL_OPTIONS}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>醫院：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-organization-id`}
                  name="organizationId"
                  value={organizationId}
                  onChange={(next) => setOrganizationId(next)}
                  options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>訓練類別：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-job-type-id`}
                  name="jobTypeId"
                  value={jobTypeId}
                  onChange={(next) => setJobTypeId(next)}
                  options={selectOptionsOverride(pageId, 'jobTypeId') ?? TRAINING_JOB_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                  {...(selectOptionsOverride(pageId, 'jobTypeId') ? {} : { placeholder: '請選擇' })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>臨床訓練計畫：
              </label>
              <div className="w-64">
                <Select
                  uiId={`${pageId}-query-plan-target-id`}
                  name="planTargetId"
                  value={planTargetId}
                  onChange={(next) => setPlanTargetId(next)}
                  // 批次 52：planTargetId 凍結真空（OPTION_SET_OVERRIDES=[]）＝0 選項（凍結鏈擋＝凍結行為）。
                  options={selectOptionsOverride(pageId, 'planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS}
                />
              </div>
            </div>
            {/* 臨床教師部門（radio 0）／訓練階段部門（radio 1）——queryDeptType 分支（:596-614）。 */}
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <input
                  type="radio"
                  data-ui-id={`${pageId}-query-dept-type-0`}
                  name="queryDeptType"
                  value="0"
                  checked={queryDeptType === '0'}
                  onChange={() => setQueryDeptType('0')}
                />
                臨床教師部門：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-clinical-dept-id`}
                  name="clinicalDeptId"
                  value={clinicalDeptId}
                  disabled={queryDeptType !== '0'}
                  onChange={(next) => setClinicalDeptId(next)}
                  // GNL-002（批次 29，issue #30）：首項鏡像 legacy 全部(-1)（training-transcript-list raw-dom）；
                  // 原 value:"" 空標籤首項改為全部(-1)。預選值差（legacy selected＝管理部門 DB 值）＝GNL-006。
                  // 批次 52 修訂：override 命中（凍結 [管理部門(1)]）即取代＋首項語意以凍結為準。
                  options={selectOptionsOverride(pageId, 'clinicalDeptId') ?? [{ value: '-1', label: '全部' }, ...ACCOUNT_DEPARTMENT_OPTIONS]}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <input
                  type="radio"
                  data-ui-id={`${pageId}-query-dept-type-1`}
                  name="queryDeptType"
                  value="1"
                  checked={queryDeptType === '1'}
                  onChange={() => setQueryDeptType('1')}
                />
                訓練階段部門：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-phase-department-id`}
                  name="phaseDepartmentId"
                  value={phaseDepartmentId}
                  disabled={queryDeptType !== '1'}
                  onChange={(next) => setPhaseDepartmentId(next)}
                  options={selectOptionsOverride(pageId, 'phaseDepartmentId') ?? [{ value: '', label: '' }, ...ACCOUNT_DEPARTMENT_OPTIONS]}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>訓練階段名稱：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-phase-target-id`}
                  name="phaseTargetId"
                  value={phaseTargetId}
                  onChange={(next) => setPhaseTargetId(next)}
                  // 批次 52：phaseTargetId 凍結真空＝0 選項。
                  options={selectOptionsOverride(pageId, 'phaseTargetId') ?? [{ value: '', label: '' }, ...TRAINING_PHASE_TARGET_OPTIONS]}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">學員：</label>
              <div className="w-40">
                <TextInput
                  uiId={`${pageId}-query-user-code`}
                  name="userCode"
                  value={userCode}
                  placeholder="(請輸入學員姓名或學員編號)"
                  onChange={(next) => setUserCode(next)}
                />
              </div>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-user-id`}
                  name="userId"
                  value={userId}
                  onChange={(next) => setUserId(next)}
                  options={selectOptionsOverride(pageId, 'userId') ?? [{ value: '', label: '' }, ...TRAINING_STUDENT_OPTIONS]}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">訓練期間：</label>
              {/* 原生 type=date（批次 14 先例：C5-h drive 相容；legacy datepicker yyyy/MM/dd →
                ISO 顯示【雛形值待實機校準】；預設空＝legacy 本月頭尾【雛形值待實機校準】）。 */}
              <input
                data-ui-id={`${pageId}-query-phase-start-date`}
                name="phaseStartDate"
                type="date"
                className="rounded border border-border px-2 py-1 text-sm"
                value={phaseStartDate}
                onChange={(event) => setPhaseStartDate(event.target.value)}
              />
              <input
                data-ui-id={`${pageId}-query-phase-end-date`}
                name="phaseEndDate"
                type="date"
                className="rounded border border-border px-2 py-1 text-sm"
                value={phaseEndDate}
                onChange={(event) => setPhaseEndDate(event.target.value)}
              />
              <Button
                uiId={`${pageId}-query-dates-reset`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setPhaseStartDate('');
                  setPhaseEndDate('');
                }}
              >
                清除
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* 查詢（btnQuery；complexBodySubmitId 自報＝C5-h 送出）。 */}
              <Button
                uiId={`${pageId}-query-btn`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => doQuery(false)}
              >
                查詢
              </Button>
              <Button
                uiId={`${pageId}-export-btn`}
                variant="default"
                size="sm"
                type="button"
                dataEffect={`list:${pageId}#export`}
                onClick={doExport}
              >
                匯出畫面資料為Excel
              </Button>
              <Button
                uiId={`${pageId}-upload-btn`}
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  setImportFile(null);
                  setImportOpen(true);
                }}
              >
                匯入學員成績
              </Button>
              <Button
                uiId={`${pageId}-calculate-btn`}
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  void (async () => {
                    // :369-376 $.confirm（title 三行逐字，type red）→doQuery(true)。
                    const confirmed = await dialogs.confirm(
                      '算完後的成績需要自己按存檔才會存入資料庫。\n如資料過多會計算的比較久，請耐心等候。\n勾審核通過要先存檔後，才不會再被異動到。',
                    );
                    if (confirmed) {
                      doQuery(true);
                    }
                  })();
                }}
              >
                計算成績
              </Button>
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-sm font-bold">說明:</p>
            <ol className="ml-5 list-decimal text-sm">
              <li>「分數為百分制，至多僅能輸入至小數第一位，超出範圍會自動四捨五入」</li>
            </ol>
          </div>
        </div>

        {/* 成績表（動態表頭＋score input＋試算；fixedHeader 不鏡像）。 */}
        <div className="mt-4">
          <Table
            uiId={`${pageId}-result-table`}
            columns={headColumns}
            rows={rows}
            rowKey={(row) => toUiKey(String(row.id ?? ''))}
            emptyText="查無符合的資料"
            renderCell={(row, columnKey) => {
              const rawKey = String(row.id ?? '');
              const rowKey = toUiKey(rawKey);
              if (columnKey === 'check') {
                const disabled = String(row['changeable'] ?? '') !== '1';
                return (
                  <Checkbox
                    uiId={`${pageId}-row-${rowKey}-changeable-check`}
                    // name 照 legacy=changeableCheckBox；雛形由 state 讀值（doSave update payload
                    // 為結構化 transcriptDetailList，非 name serialize）→ 不帶 name（C5-h 分母
                    // 只計帶 name 控制項；批次 14 reportControls「非 serialize 欄不帶 name」先例）。
                    checked={changeables[rawKey] === true}
                    disabled={disabled}
                    onChange={(next) => setChangeables((current) => ({ ...current, [rawKey]: next }))}
                  />
                );
              }
              if (columnKey === 'period') {
                return (
                  <span>
                    {`${String(row['phaseStartDateStr'] ?? '')}~${String(row['phaseEndDateStr'] ?? '')}`}
                    <div>{String(row['phaseName'] ?? '')}</div>
                  </span>
                );
              }
              if (columnKey === 'employee') {
                const schoolId = String(row['schoolId'] ?? '');
                return `${String(row['studentEmployeeId'] ?? '')}${schoolId === '' ? '' : `(${schoolId})`}`;
              }
              if (columnKey === 'student') {
                return String(row['studentName'] ?? '');
              }
              if (columnKey === 'clinical') {
                return String(row['clinicalName'] ?? '') === '' ? <span>&nbsp;</span> : String(row['clinicalName'] ?? '');
              }
              if (columnKey.startsWith('target-')) {
                const targetId = columnKey.slice('target-'.length);
                const disabled = String(row['changeable'] ?? '') !== '1';
                return (
                  <TextInput
                    uiId={`${pageId}-row-${rowKey}-score-${targetId}`}
                    // legacy name=calculateName（serialize 收值）；雛形由 state 讀值→不帶 name
                    // （C5-h 同上，批次 14 reportControls 先例）。
                    value={scoreDrafts[rawKey]?.[targetId] ?? ''}
                    maxLength={3}
                    disabled={disabled}
                    onChange={(next) => changeScore(rawKey, targetId, next)}
                  />
                );
              }
              if (columnKey === 'final') {
                return (
                  <TextInput
                    uiId={`${pageId}-row-${rowKey}-final-num`}
                    // legacy name=finalNum；disabled 試算欄由 state 算出→不帶 name（C5-h 同上）。
                    value={finalNums[rawKey] ?? ''}
                    disabled
                    onChange={() => undefined}
                  />
                );
              }
              return '';
            }}
          />
          <div className="mt-2 text-right">
            {/* 存檔（btnSave :734）。 */}
            <Button
              uiId={`${pageId}-save-btn`}
              variant="primary"
              size="sm"
              type="button"
              disabled={busy}
              dataEffect={`update:${pageId}`}
              onClick={doSave}
            >
              存檔
            </Button>
          </div>
        </div>
      </ContentPanel>

      {/* 匯入學生成績 modal（dialogModal :748-836）。 */}
      <ModalFrame
        uiId={`${pageId}-import-dialog`}
        open={importOpen}
        title="匯入學生成績"
        size="lg"
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <Button uiId={`${pageId}-import-cancel`} variant="default" size="sm" type="button" onClick={() => setImportOpen(false)}>
              關閉
            </Button>
            <Button uiId={`${pageId}-import-submit`} variant="primary" size="sm" type="button" onClick={runImport}>
              匯入
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">
              <span className="text-red-600">*</span>學年度：
            </label>
            <div className="w-48">
              <Select uiId={`${pageId}-import-annual-id`} value={annualId} onChange={() => undefined} options={COURSE_CLAZZ_ANNUAL_OPTIONS} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">
              <span className="text-red-600">*</span>醫院：
            </label>
            <div className="w-48">
              <Select uiId={`${pageId}-import-organization-id`} value={organizationId} onChange={() => undefined} options={ACCOUNT_ORGANIZATION_OPTIONS} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">
              <span className="text-red-600">*</span>訓練類別：
            </label>
            <div className="w-48">
              <Select
                uiId={`${pageId}-import-job-type-id`}
                value={jobTypeId}
                onChange={() => undefined}
                options={TRAINING_JOB_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">
              <span className="text-red-600">*</span>臨床訓練計畫：
            </label>
            <div className="w-64">
              <Select uiId={`${pageId}-import-plan-target-id`} value={planTargetId} onChange={() => undefined} options={COURSE_CLAZZ_PLAN_TARGET_OPTIONS} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">
              <span className="text-red-600">*</span>訓練階段名稱：
            </label>
            <div className="w-48">
              <Select
                uiId={`${pageId}-import-phase-target-id`}
                value={importPhaseTargetId === '' ? phaseTargetId : importPhaseTargetId}
                onChange={(next) => setImportPhaseTargetId(next)}
                options={[{ value: '', label: '' }, ...TRAINING_PHASE_TARGET_OPTIONS]}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">訓練期間：</label>
            <div className="w-36">
              <TextInput uiId={`${pageId}-import-phase-start-date`} value={TODAY} readOnly maxLength={10} onChange={() => undefined} />
            </div>
            <div className="w-36">
              <TextInput uiId={`${pageId}-import-phase-end-date`} value={TODAY} readOnly maxLength={10} onChange={() => undefined} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">檔案匯入：</label>
            <input
              type="file"
              data-ui-id={`${pageId}-import-file`}
              className="text-xs"
              onChange={(event) => {
                setImportFile(event.target.files?.[0] ?? null);
              }}
            />
          </div>
        </div>
      </ModalFrame>
    </div>
  );
}
